import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { DataTable, type Column } from '@/components/shared/DataTable';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { FilterBar, type FilterOption } from '@/components/shared/FilterBar';
import { useCurrency } from '@/hooks/useCurrency';
import { useWarehouseId } from '@/contexts/WarehouseContext';
import { useVisibleProductCatFilters } from '@/hooks/useVisibleProductCatFilters';
import { ClassificationBadges, ColumnHeader } from '@/modules/forecast-review/components/ForecastReviewTableCells';
import type { SafetyStockPolicyRow } from '@/services/safetyStockService';
import { SkuDetailPanel } from '../components/SkuDetailPanel';
import {
  useSafetyStockPolicies,
  useSafetyStockPolicyFilters,
} from '../hooks/useSafetyStockPolicies';
import {
  formatDays,
  formatDemandClassification,
  formatPercentFraction,
  formatSafetyStockMethod,
  formatUnits,
  toNumber,
} from '../utils/format';

function firstValue(values: string[] | undefined): string | undefined {
  return values?.[0];
}

function demandBadgeClass(raw: string | null | undefined): string {
  const v = (raw || '').trim().toLowerCase();
  if (v === 'smooth') return 'bg-emerald-50 text-emerald-800';
  if (v === 'intermittent') return 'bg-amber-50 text-amber-800';
  if (v === 'erratic') return 'bg-orange-50 text-orange-800';
  if (v === 'lumpy') return 'bg-red-50 text-red-800';
  if (v === 'new') return 'bg-sky-50 text-sky-800';
  if (v === 'insufficient_history') return 'bg-violet-50 text-violet-800';
  return 'bg-gray-100 text-gray-700';
}

function abcXyzParts(
  row: SafetyStockPolicyRow,
): { abc: string | null; xyz: string | null } {
  const abc = (row.abc_class || '').trim().toUpperCase() || null;
  const xyz = (row.xyz_class || '').trim().toUpperCase() || null;
  if (abc || xyz) return { abc, xyz };
  const raw = (row.abc_xyz || '').trim().toUpperCase();
  if (raw.length >= 2) return { abc: raw[0], xyz: raw[1] };
  if (raw.length === 1) return { abc: raw[0], xyz: null };
  return { abc: null, xyz: null };
}

export function SafetyStockProductsPage() {
  const { t, i18n } = useTranslation();
  const { format, symbol } = useCurrency();
  const locale = i18n.language || 'es';
  const warehouseId = useWarehouseId();
  const [searchParams, setSearchParams] = useSearchParams();
  const visibleCats = useVisibleProductCatFilters();

  const initialAbc = (searchParams.get('abc_xyz') || '').toUpperCase();
  const initialWarnings = searchParams.get('has_warnings') === 'true';
  const [filters, setFilters] = useState<Record<string, string[]>>(() => {
    const init: Record<string, string[]> = {};
    if (initialAbc) init.abc_xyz = [initialAbc];
    if (initialWarnings) init.has_warnings = ['true'];
    return init;
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [sort, setSort] = useState('product_name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedId, setSelectedId] = useState<number | null>(null);

  useEffect(() => {
    const abc = (searchParams.get('abc_xyz') || '').toUpperCase();
    const warnings = searchParams.get('has_warnings') === 'true';
    setFilters((prev) => {
      let next = prev;
      let changed = false;
      if (abc && prev.abc_xyz?.[0] !== abc) {
        next = { ...next, abc_xyz: [abc] };
        changed = true;
      }
      if (warnings && prev.has_warnings?.[0] !== 'true') {
        next = { ...next, has_warnings: ['true'] };
        changed = true;
      }
      return changed ? next : prev;
    });
    if (abc || warnings) setPage(1);
  }, [searchParams]);

  useEffect(() => {
    setPage(1);
    setSelectedId(null);
  }, [warehouseId]);

  const filtersQuery = useSafetyStockPolicyFilters();

  const apiParams = useMemo(() => {
    const params: Record<string, string | number | boolean | string[]> = {
      page,
      page_size: pageSize,
      sort,
      sort_dir: sortDir,
    };
    if (typeof warehouseId === 'number') {
      params.warehouse_id = warehouseId;
    }
    const product = firstValue(filters.product_id);
    if (product) params.product_id = product;
    const supplier = filters.supplier_id;
    if (supplier?.length) params.supplier_id = supplier.join(',');
    const family = filters.family;
    if (family?.length) params.family = family.join(',');
    const abc = filters.abc_xyz;
    if (abc?.length) params.abc_xyz = abc.join(',');
    const demand = filters.demand_classification;
    if (demand?.length) params.demand_classification = demand.join(',');
    const method = filters.method;
    if (method?.length) params.method = method.join(',');
    if (firstValue(filters.has_warnings) === 'true') params.has_warnings = true;
    for (const field of visibleCats) {
      const values = filters[field];
      if (values?.length) params[field] = values.join(',');
    }
    return params;
  }, [filters, page, pageSize, sort, sortDir, visibleCats, warehouseId]);

  const policiesQuery = useSafetyStockPolicies(
    apiParams,
    typeof warehouseId === 'number',
  );
  const rows = policiesQuery.data?.results || [];
  const selected = rows.find((r) => r.id === selectedId) || null;

  const filterOptions: FilterOption[] = useMemo(() => {
    const catalog = filtersQuery.data;
    const opts: FilterOption[] = [
      {
        id: 'supplier_id',
        name: t('safetyStock.products.filters.supplier'),
        multiple: true,
        options: (catalog?.suppliers || []).map((o) => ({
          value: String(o.value),
          label: o.label,
        })),
      },
      {
        id: 'family',
        name: t('safetyStock.products.filters.family'),
        multiple: true,
        options: (catalog?.families || []).map((o) => ({
          value: String(o.value),
          label: o.label,
        })),
      },
      {
        id: 'abc_xyz',
        name: t('safetyStock.products.filters.abcXyz'),
        multiple: true,
        options: (catalog?.abc_xyz || []).map((o) => ({
          value: String(o.value),
          label: o.label,
        })),
      },
      {
        id: 'demand_classification',
        name: t('safetyStock.products.filters.demand'),
        multiple: true,
        options: (catalog?.demand_classifications || []).map((o) => ({
          value: String(o.value),
          label: formatDemandClassification(String(o.value), t),
        })),
      },
      {
        id: 'method',
        name: t('safetyStock.products.filters.method'),
        multiple: true,
        options: (catalog?.methods || []).map((o) => ({
          value: String(o.value),
          label: formatSafetyStockMethod(String(o.value), t),
        })),
      },
      {
        id: 'has_warnings',
        name: t('safetyStock.products.filters.hasWarnings'),
        multiple: false,
        options: [{ value: 'true', label: t('safetyStock.products.filters.warningsOnly') }],
      },
    ];
    for (const field of visibleCats) {
      opts.push({
        id: field,
        name: t(`productCatFilters.${field}`),
        multiple: true,
        options: [],
        loadOptionsRemotely: false,
      });
    }
    return opts;
  }, [filtersQuery.data, t, visibleCats]);

  const activeFilters = useMemo(() => {
    const chips: Array<{ id: string; label: string; value: string }> = [];
    for (const [id, values] of Object.entries(filters)) {
      for (const value of values) {
        const filterMeta = filterOptions.find((f) => f.id === id);
        const optLabel =
          filterMeta?.options.find((o) => String(o.value) === String(value))?.label ||
          value;
        chips.push({
          id: `${id}:${value}`,
          label: `${filterMeta?.name || id}: ${optLabel}`,
          value: String(value),
        });
      }
    }
    return chips;
  }, [filters, filterOptions]);

  const columns: Column[] = useMemo(
    () => [
      {
        header: t('safetyStock.products.cols.product'),
        accessor: 'product_name',
        sortable: true,
        width: '170px',
        cell: (_v, row: SafetyStockPolicyRow) => (
          <div className="min-w-0 max-w-full">
            <div className="truncate text-xs font-medium text-gray-900" title={row.product_name || undefined}>
              {row.product_name || '—'}
            </div>
            {row.product_code ? (
              <div className="truncate text-[10px] text-gray-500" title={row.product_code}>
                {row.product_code}
              </div>
            ) : null}
          </div>
        ),
        exportHeader: t('safetyStock.products.cols.product'),
        exportValue: (_v, row: SafetyStockPolicyRow) =>
          [row.product_code, row.product_name].filter(Boolean).join(' · '),
      },
      {
        header: t('safetyStock.products.cols.supplier'),
        accessor: 'supplier_name',
        sortable: true,
        width: '110px',
        cell: (v) => (
          <span className="block truncate text-xs text-gray-800" title={(v as string) || undefined}>
            {(v as string) || '—'}
          </span>
        ),
      },
      {
        header: t('safetyStock.products.cols.abcXyz'),
        accessor: 'abc_xyz',
        sortable: true,
        width: '72px',
        cell: (_v, row: SafetyStockPolicyRow) => {
          const { abc, xyz } = abcXyzParts(row);
          return (
            <ClassificationBadges
              classification={{
                abc_quantity: abc,
                xyz_class: xyz,
              }}
            />
          );
        },
        exportValue: (_v, row: SafetyStockPolicyRow) => {
          const { abc, xyz } = abcXyzParts(row);
          return [abc, xyz].filter(Boolean).join('') || null;
        },
      },
      {
        header: t('safetyStock.products.cols.demand'),
        accessor: 'demand_classification',
        sortable: true,
        cell: (v) => {
          const raw = (v as string | null | undefined) || '';
          if (!raw) return <span className="text-[10px] text-gray-400">—</span>;
          return (
            <span
              className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-medium ${demandBadgeClass(raw)}`}
            >
              {formatDemandClassification(raw, t)}
            </span>
          );
        },
        exportValue: (v) =>
          formatDemandClassification(v as string | null | undefined, t),
      },
      {
        header: (
          <ColumnHeader
            title={t('safetyStock.products.cols.recentSales')}
            subtitle={policiesQuery.data?.recent_period_label || undefined}
            align="right"
          />
        ),
        exportHeader: t('safetyStock.products.cols.recentSales'),
        accessor: 'recent_total',
        numeric: true,
        align: 'right',
        cell: (v) => {
          const n = toNumber(v);
          if (n == null) return <span className="text-[10px] text-gray-400">—</span>;
          return (
            <span className="text-xs font-medium tabular-nums text-gray-800">
              {formatUnits(n, locale)}
            </span>
          );
        },
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.method'),
        accessor: 'method',
        sortable: true,
        cell: (v) => (
          <span className="inline-flex max-w-[10rem] truncate rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
            {formatSafetyStockMethod(v as string | null | undefined, t)}
          </span>
        ),
        exportValue: (v) => formatSafetyStockMethod(v as string | null | undefined, t),
      },
      {
        header: t('safetyStock.products.cols.csl'),
        accessor: 'service_level',
        numeric: true,
        align: 'right',
        sortable: true,
        cell: (v) => (
          <span className="text-xs tabular-nums">{formatPercentFraction(v, locale)}</span>
        ),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.safetyStock'),
        accessor: 'safety_stock',
        numeric: true,
        align: 'right',
        sortable: true,
        cell: (v) => (
          <span className="text-xs tabular-nums">{formatUnits(v, locale)}</span>
        ),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.value', { currencySymbol: symbol }),
        accessor: 'safety_stock_value',
        numeric: true,
        align: 'right',
        cell: (v) => (
          <span className="text-xs tabular-nums">
            {toNumber(v) == null ? '—' : format(v)}
          </span>
        ),
        exportType: 'currency',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.coverageDays'),
        accessor: 'coverage_days',
        numeric: true,
        align: 'right',
        cell: (v) => (
          <span className="text-xs tabular-nums">{formatDays(v, locale)}</span>
        ),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.protection'),
        accessor: 'protection_period_days',
        numeric: true,
        align: 'right',
        cell: (v) => (
          <span className="text-xs tabular-nums">{formatDays(v, locale)}</span>
        ),
        exportType: 'number',
        exportValue: (v) => toNumber(v),
      },
      {
        header: t('safetyStock.products.cols.updatedAt'),
        accessor: 'updated_at',
        sortable: true,
        width: '108px',
        cell: (v) => {
          if (!v) return <span className="text-[10px] text-gray-400">—</span>;
          try {
            return (
              <span className="text-[10px] text-gray-600">
                {new Date(String(v)).toLocaleString(locale, {
                  dateStyle: 'short',
                  timeStyle: 'short',
                })}
              </span>
            );
          } catch {
            return <span className="text-[10px]">{String(v)}</span>;
          }
        },
      },
      {
        header: t('safetyStock.products.cols.updateType'),
        accessor: 'update_type',
        sortable: true,
        width: '96px',
        cell: (v) => {
          const raw = (v as string | null | undefined) || '';
          if (!raw) return <span className="text-[10px] text-gray-400">—</span>;
          const code = raw.trim().toUpperCase();
          return (
            <span className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
              {t(`safetyStock.updateTypes.${code}`, { defaultValue: code })}
            </span>
          );
        },
        exportValue: (v) => {
          const raw = (v as string | null | undefined) || '';
          if (!raw) return null;
          const code = raw.trim().toUpperCase();
          return t(`safetyStock.updateTypes.${code}`, { defaultValue: code });
        },
      },
    ],
    [t, locale, format, symbol, policiesQuery.data?.recent_period_label],
  );

  const totalPages = Math.max(
    1,
    Math.ceil((policiesQuery.data?.total || 0) / pageSize),
  );

  const handleSort = (field: string) => {
    if (sort === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="flex flex-col gap-6 p-4 md:p-6">
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">
          {t('safetyStock.products.title')}
        </h2>
        <FilterBar
          filters={filterOptions}
          activeFilters={activeFilters}
          onFilterChange={(next) => {
            setFilters(next);
            setPage(1);
            const abc = next.abc_xyz?.[0];
            if (abc) {
              setSearchParams({ abc_xyz: abc }, { replace: true });
            } else if (searchParams.get('abc_xyz')) {
              setSearchParams({}, { replace: true });
            }
          }}
          onClearFilter={(filterId) => {
            setFilters((prev) => {
              const copy = { ...prev };
              // chip id is filterId:value — FilterBar may pass filter id only via onClearFilter
              const key = filterId.includes(':') ? filterId.split(':')[0] : filterId;
              const value = filterId.includes(':') ? filterId.split(':').slice(1).join(':') : null;
              if (value == null) {
                delete copy[key];
              } else {
                copy[key] = (copy[key] || []).filter((v) => v !== value);
                if (!copy[key]?.length) delete copy[key];
              }
              return copy;
            });
            setPage(1);
          }}
          onClearAllFilters={() => {
            setFilters({});
            setPage(1);
            setSearchParams({}, { replace: true });
          }}
        />
      </section>

      {policiesQuery.isError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {t('safetyStock.products.loadError')}
        </div>
      ) : null}

      <div className="text-sm text-gray-600">
        {policiesQuery.isLoading ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('safetyStock.products.loading')}
          </span>
        ) : (
          t('safetyStock.products.count', { count: policiesQuery.data?.total ?? 0 })
        )}
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={policiesQuery.isLoading}
        allowExport
        exportFilename="safety-stock-policies"
        compact
        resizableColumns
        onRowClick={(row: SafetyStockPolicyRow) => setSelectedId(row.id)}
        rowClassName={(row: SafetyStockPolicyRow) =>
          row.id === selectedId ? 'bg-sky-50' : ''
        }
        externalPagination={{
          currentPage: page,
          totalPages,
          totalCount: policiesQuery.data?.total ?? 0,
          pageSize,
          onPageChange: setPage,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPage(1);
          },
        }}
        externalSorting={{
          field: sort,
          direction: sortDir,
          onSort: handleSort,
        }}
      />

      {selected ? (
        <SkuDetailPanel row={selected} onClose={() => setSelectedId(null)} />
      ) : (
        <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
          {t('safetyStock.products.selectHint')}
        </div>
      )}
    </div>
  );
}
